// This mixin adds fields that support a model type with associated ***tagvalues records,
// for example Resource, Task, Client.
import { TAGS_KEYS } from "../../../other/constants/TagsKeys.jsx";

export const ModelTagsMixin = (target) =>
  class extends target {
    static get fields() {
      return [
        // So, we can't have a `name` in one item matching its `datasource` name another item, If we do then we'll experience an err behavior
        { name: "categoryId", dataSource: "category" },
        { name: "tagsId", dataSource: "tag" },
        { name: "statusId", dataSource: "status" },
        { name: "teamId", dataSource: "team" },
        { name: "phaseId", dataSource: "phase" },
        // local fields only
        { name: "_category", persist: false },
        { name: "_status", persist: false },
        { name: "_tags", persist: false },
        { name: "_team", persist: false },
        { name: "_phase", persist: false },
        // display fields for grid
        { name: "tagsValue", persist: false },
        { name: "categoryValue", persist: false },
        { name: "statusValue", persist: false },
        { name: "teamValue", persist: false },
        { name: "teamColor", persist: false },
        { name: "phaseValue", persist: false },
      ];
    }

    static swivuGetFieldOriginalSourceName(fieldName) {
      const fieldsMaps = {
        _category: "category",
        _status: "status",
        _tags: "tag",
        _team: "team",
        categoryValue: "category",
        statusValue: "status",
        tagsValue: "tag",
        teamValue: "team",
        phaseValue: "phase"
      }
      return fieldsMaps[fieldName] || super.swivuGetFieldOriginalSourceName?.(fieldName);
    }

    get tagStore() {
      const project = this.project?.mainProject ?? this.project;

      return project?.getCrudStore(this.tagStoreName);
    }

    get teamStore() {
      return this.project && this.project.teamStore;
    }

    getTagValue(store, type, isArrayTypeField = false) {
      const entityId = this[`${type}Id`];
      if (store && entityId) {
        if (isArrayTypeField) {
          if (Array.isArray(entityId)) {
            return store.query((q) => entityId.includes(q.id));
          }
        } else {
          return store.getById(entityId);
        }
      }
    }

    get _category() {
      return this.getTagValue(this.tagStore, "category");
    }
    set _category(category) {
      const categoryId =
        category && typeof category === "object"
          ? category.id
          : category || null;
      const categoryValue = categoryId
        ? this.tagStore.getById(categoryId)?.tagValue ?? null
        : null;
      this.set({
        categoryId,
        _category: null,
        categoryValue
      });
    }

    get _status() {
      return this.getTagValue(this.tagStore, "status");
    }
    set _status(status) {
      const statusId =
        status && typeof status === "object" ? status.id : status || null;
      const statusValue = statusId
        ? this.tagStore.getById(statusId)?.tagValue ?? null
        : null;
      this.set({
        statusId,
        _status: null,
        statusValue
      });
    }

    get _phase() {
      return this.getTagValue(this.tagStore, "phase");
    }
    set _phase(phase) {
      const phaseId =
        phase && typeof phase === "object" ? phase.id : phase || null;
      const phaseValue = phaseId
        ? this.tagStore.getById(phaseId)?.tagValue ?? null
        : null;
      this.set({
        phaseId,
        _phase: null,
        phaseValue
      });
    }

    get _tags() {
      return this.getTagValue(this.tagStore, "tags",true);
    }
    set _tags(tags) {
      const tagsId = tags && Array.isArray(tags) ? tags : this.get("tagsId");
      const tagsValue = tagsId
        ? tagsId.map((id) => this.tagStore.getById(id)?.tagValue)
        : null;
      this.set({
        tagsId,
        _tags: null,
        tagsValue
      });
    }

    get _team() {
      return this.getTagValue(this.teamStore, "team");
    }
    set _team(team) {
      const teamId = team && typeof team === "object" ? team.id : team || null;
      const teamInstance = teamId ? this.teamStore.getById(teamId) : null;
      const teamValue = teamInstance ? teamInstance?.name ?? null : null;
      const teamColor = teamInstance ? teamInstance?.color ?? null : null;

      this.set({
        teamId,
        _team: null,
        // This field is used by the column. We need to update it in order to give features,
        // like grouping, opportunity to react to a change.
        teamValue,
        teamColor
      });
    }

    // value fields for grid
    get tagsValue() {
      return this._tags?.map((tag) => tag.tagValue) || [];
    }
    set tagsValue(values) {
      // I could have use store `query` function and values.includes, but then we need to handle duplicate find cases since query iterate and search all records
      this._tags =
        Array.isArray(values) && values.length
          ? values.map(
              (tagToSet) =>
                this.tagStore.find(
                  (tag) =>
                    tag.tagSet === TAGS_KEYS.tag && tag.tagValue === tagToSet
                ).id
            )
          : [];
    }

    get categoryValue() {
      return this._category?.tagValue;
    }
    set categoryValue(value) {
      this._category = value
        ? this.tagStore.find(
            (tag) => tag.tagSet === TAGS_KEYS.category && tag.tagValue === value
          )
        : null;
    }

    get phaseValue() {
      return this._phase?.tagValue;
    }
    set phaseValue(value) {
      this._phase = value
        ? this.tagStore.find(
          (tag) => tag.tagSet === TAGS_KEYS.phase && tag.tagValue === value
        )
        : null;
    }

    get statusValue() {
      return this._status?.tagValue;
    }
    set statusValue(value) {
      this._status = value
        ? this.tagStore.find(
            (tag) => tag.tagSet === TAGS_KEYS.status && tag.tagValue === value
          )
        : null;
    }

    get teamValue() {
      return this._team?.name;
    }

    // setter

    // we need to override setter for our plain values since some bryntum features automatically set value
    // on grid field such as a when grouping is applied on our custom Tag column and then re-order rows to
    // drop on other grouping, bryntum will try to update that field by using field key of that column

    set teamValue(value) {
      this._team = value
        ? this.teamStore.find((team) => team.name === value)
        : null;
    }

    get teamColor() {
      return this._team?.color
    }
  };
